/**
 * Gift note line
 *
 * When the cart note (the customer's gift note) is filled in, keeps exactly
 * one free "Gift note" line in the cart so the ERP can flag the order. The
 * line is removed when the note is cleared or no other items remain.
 *
 * Config comes from snippets/gift-note-line.liquid via window.giftNoteLine.
 */
(function () {
  const config = window.giftNoteLine;

  if (!config || !config.variantId || window.giftNoteLineLoaded) return;
  window.giftNoteLineLoaded = true;

  const GIFT_PROPERTY = "_gift_note";
  const MESSAGE_PROPERTY = "Gift message";
  const NOTE_SELECTOR = 'textarea[name="note"]';
  const CHECKOUT_SELECTOR = 'button[name="checkout"]';
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const NOTE_DEBOUNCE = 600;
  const CART_CHANGE_DEBOUNCE = 300;
  const CHECKOUT_TIMEOUT = 4000;

  const rootUrl = (config.rootUrl || "/").replace(/\/?$/, "/");
  const nativeFetch = window.fetch.bind(window);
  const pendingCartRequests = new Set();

  let queue = Promise.resolve();
  let queuedSync = null;
  let addBlocked = false;
  let hasWarned = false;
  let checkoutPending = false;
  // False after we change the cart until the cart markup is re-rendered, so
  // checkout never posts the form's positional updates[] against stale lines.
  let markupInSync = true;

  function warnOnce(message, error) {
    if (hasWarned) return;
    hasWarned = true;
    console.warn(`[Gift note] ${message}`, error || "");
  }

  function debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Runs tasks one at a time so fast typing or quick cart changes can't
  // race each other into duplicate gift lines.
  function enqueue(task) {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  }

  // Coalesces sync requests: if one is already waiting in the queue, callers
  // share it instead of queueing another.
  function requestSync() {
    if (queuedSync) return queuedSync;

    queuedSync = enqueue(() => {
      queuedSync = null;
      return syncGiftNoteLine();
    }).catch((error) => {
      warnOnce("Could not update the gift note line.", error);
    });

    return queuedSync;
  }

  const requestSyncAfterNoteInput = debounce(requestSync, NOTE_DEBOUNCE);
  const requestSyncAfterCartChange = debounce(requestSync, CART_CHANGE_DEBOUNCE);

  async function cartRequest(path, body) {
    const response = await nativeFetch(`${rootUrl}${path}`, {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const error = new Error((data && (data.description || data.message)) || response.statusText);
      error.status = response.status;
      throw error;
    }

    return data;
  }

  function isGiftLine(item) {
    return item.variant_id === config.variantId;
  }

  function getNoteField() {
    return document.querySelector(NOTE_SELECTOR);
  }

  function lineIsCorrect(item, message) {
    const properties = item.properties || {};

    return (
      item.quantity === 1 &&
      properties[GIFT_PROPERTY] === "true" &&
      properties[MESSAGE_PROPERTY] === message
    );
  }

  async function syncGiftNoteLine() {
    let cart = await cartRequest("cart.js");

    // Save the note from the visible field first, so the line always matches
    // what the customer typed even if the field hasn't been saved yet.
    const noteField = getNoteField();
    if (noteField && noteField.value !== (cart.note || "")) {
      cart = await cartRequest("cart/update.js", { note: noteField.value });
    }

    const message = (cart.note || "").trim();
    const giftLines = cart.items.filter(isGiftLine);
    const hasRealItems = cart.items.some((item) => !isGiftLine(item));
    const wanted = message !== "" && hasRealItems;
    const [keep, ...extras] = giftLines;
    const toRemove = wanted ? extras : giftLines;
    let changed = false;

    if (toRemove.length) {
      const updates = {};
      toRemove.forEach((item) => {
        updates[item.key] = 0;
      });
      markupInSync = false;
      cart = await cartRequest("cart/update.js", { updates });
      changed = true;
    }

    if (wanted && keep && !lineIsCorrect(keep, message)) {
      markupInSync = false;
      cart = await cartRequest("cart/change.js", {
        id: keep.key,
        quantity: 1,
        properties: { [MESSAGE_PROPERTY]: message, [GIFT_PROPERTY]: "true" },
      });
      changed = true;
    }

    if (wanted && !keep && !addBlocked) {
      try {
        markupInSync = false;
        await cartRequest("cart/add.js", {
          items: [
            {
              id: config.variantId,
              quantity: 1,
              properties: { [MESSAGE_PROPERTY]: message, [GIFT_PROPERTY]: "true" },
            },
          ],
        });
        cart = await cartRequest("cart.js");
        changed = true;
      } catch (error) {
        // Most likely the variant ID is wrong or the product is unavailable.
        // Stop retrying on every keystroke; checkout still works without it.
        addBlocked = true;
        if (!changed) markupInSync = true;
        warnOnce(
          `Could not add the gift note product (variant ${config.variantId}). Check the "Gift note variant ID" theme setting and that the product is active and available on the Online Store.`,
          error
        );
      }
    }

    if (changed) {
      await renderCart(cart);
    }
  }

  function parseSection(html) {
    return new DOMParser().parseFromString(html, "text/html");
  }

  function replaceInner(target, source) {
    if (target && source) target.innerHTML = source.innerHTML;
  }

  function replaceOuter(target, source) {
    if (target && source) target.outerHTML = source.outerHTML;
  }

  // Re-renders only the parts the gift line affects (line items and counts),
  // using the Section Rendering API like the rest of the theme. The note
  // field is left alone so the customer never loses what they are typing,
  // unless the cart is now empty and the whole cart needs redrawing.
  async function renderCart(cart) {
    const mainCartItems = document.getElementById("Cart-CartItems");
    const mainSectionId = mainCartItems && mainCartItems.dataset.id;
    const drawer = document.querySelector("cart-drawer");
    const isEmpty = cart.item_count === 0;
    const sectionIds = ["cart-icon-bubble"];

    if (mainSectionId) sectionIds.push(mainSectionId);
    if (drawer) sectionIds.push("cart-drawer");

    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("sections", sectionIds.join(","));

    // No JSON Accept header: on product pages that returns the product JSON
    // instead of the rendered sections.
    const response = await nativeFetch(url.toString());
    const sections = response.ok ? await response.json().catch(() => null) : null;
    if (!sections || !sectionIds.every((id) => id in sections)) {
      throw new Error(`Section render failed (${response.status})`);
    }

    if (sections["cart-icon-bubble"]) {
      const bubble = parseSection(sections["cart-icon-bubble"]).querySelector(".shopify-section");
      replaceInner(document.getElementById("cart-icon-bubble"), bubble);
    }

    if (mainSectionId && sections[mainSectionId]) {
      const html = parseSection(sections[mainSectionId]);
      const selectors = isEmpty ? [".js-cart-header", ".js-cart-items", ".js-cart-footer"] : [".js-cart-items"];

      selectors.forEach((selector) => {
        replaceOuter(document.querySelector(`#main-cart ${selector}`), html.querySelector(selector));
      });
    }

    if (drawer && sections["cart-drawer"]) {
      const html = parseSection(sections["cart-drawer"]);

      if (isEmpty) {
        replaceInner(drawer.querySelector("#CartDrawer"), html.querySelector("#CartDrawer"));
        drawer.querySelector("#CartDrawer-Overlay")?.addEventListener("click", drawer.close.bind(drawer));
      } else {
        replaceInner(drawer.querySelector("#CartDrawer-CartItems"), html.querySelector("#CartDrawer-CartItems"));
        replaceInner(
          drawer.querySelector(".drawer__header .js-cart-item-count"),
          html.querySelector(".drawer__header .js-cart-item-count")
        );
      }

      drawer.classList.toggle("is-empty", isEmpty);
      drawer.querySelector("cart-drawer-items")?.classList.toggle("is-empty", isEmpty);
    }

    markupInSync = true;
  }

  // Watch cart changes made by the theme, Doofinder and apps, so removing the
  // last real item also removes the gift line. Our own requests use
  // nativeFetch, so they never trigger this.
  function requestUrl(resource) {
    if (typeof resource === "string") return resource;
    if (resource instanceof URL) return resource.href;
    return (resource && resource.url) || "";
  }

  window.fetch = function (resource, options) {
    const request = nativeFetch(resource, options);

    if (CART_MUTATION_PATTERN.test(requestUrl(resource).split("#")[0])) {
      const tracked = request
        .then((response) => {
          if (response.ok) requestSyncAfterCartChange();
        })
        .catch(() => {})
        .finally(() => pendingCartRequests.delete(tracked));
      pendingCartRequests.add(tracked);
    }

    return request;
  };

  document.addEventListener("cart-change", requestSyncAfterCartChange);

  document.addEventListener("input", (event) => {
    if (event.target.matches && event.target.matches(NOTE_SELECTOR)) {
      requestSyncAfterNoteInput();
    }
  });

  document.addEventListener("focusout", (event) => {
    if (event.target.matches && event.target.matches(NOTE_SELECTOR)) {
      requestSync();
    }
  });

  // Checkout: save the note and finish any sync before leaving the cart, so
  // the gift line is never missing. Never blocks checkout for longer than
  // CHECKOUT_TIMEOUT, and carries on even if the sync fails.
  document.addEventListener(
    "click",
    (event) => {
      const button = event.target.closest && event.target.closest(CHECKOUT_SELECTOR);
      if (!button || button.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      if (checkoutPending) return;
      checkoutPending = true;
      button.setAttribute("aria-busy", "true");

      const ready = Promise.allSettled(Array.from(pendingCartRequests)).then(() => requestSync());

      Promise.race([ready, delay(CHECKOUT_TIMEOUT)]).finally(() => {
        const form = button.form;

        if (markupInSync && form && typeof form.requestSubmit === "function") {
          form.requestSubmit(button);
        } else {
          window.location.href = `${rootUrl}checkout`;
        }

        // Reset in case the customer comes back with the browser's back button.
        setTimeout(() => {
          checkoutPending = false;
          button.removeAttribute("aria-busy");
        }, 1000);
      });
    },
    true
  );

  if (config.needsSync) {
    requestSync();
  }
})();
